import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight, Play, Sparkles, CheckCircle } from "lucide-react";

const HERO_IMAGE = "https://images.unsplash.com/photo-1557682250-33bd709cbe85";

const highlights = [
  "SEO-friendly website architecture",
  "Conversion-focused design strategy",
  "Dedicated technical support",
];

export const HeroSection = () => {
  return (
    <section className="relative min-h-[calc(100vh-84px)] lg:min-h-[calc(100vh-92px)] flex items-center overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-foreground/95 via-foreground/90 to-foreground/80" />

        {/* Animated Gradient Orbs */}
        <motion.div
          className="hidden sm:block absolute top-12 right-8 md:top-16 md:right-16 w-56 h-56 md:w-80 md:h-80 rounded-full opacity-30"
          style={{
            background:
              "radial-gradient(circle, hsl(var(--primary)) 0%, transparent 70%)",
          }}
          animate={{ scale: [1, 1.2, 1], x: [0, 30, 0], y: [0, -20, 0] }}
          transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
        />
        <motion.div
          className="hidden sm:block absolute bottom-10 left-8 md:bottom-16 md:left-16 w-44 h-44 md:w-64 md:h-64 rounded-full opacity-20"
          style={{
            background:
              "radial-gradient(circle, hsl(var(--secondary)) 0%, transparent 70%)",
          }}
          animate={{ scale: [1, 1.3, 1], x: [0, -20, 0], y: [0, 30, 0] }}
          transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
        />
      </div>

      <div className="container-custom relative z-10 py-6 sm:py-8 lg:py-10">
        <div className="max-w-4xl mx-auto text-center">
          {/* Badge */}
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-3 sm:px-4 py-2 rounded-full bg-primary/20 text-primary-foreground border border-primary/30 mb-5 sm:mb-6"
          >
            <Sparkles className="h-4 w-4" />
            <span className="text-sm font-medium">
              Your Digital Success Partner
            </span>
          </motion.div>

          {/* Headline */}
          <motion.h1
            initial={{ opacity: 0, y: 26 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-[30px] leading-[1.08] sm:text-5xl lg:text-[64px] font-display font-bold text-background mb-4 sm:mb-5"
          >
            Digital Agency Vancouver Focused on{" "}
            <span className="gradient-text">Website Development & SEO</span>
          </motion.h1>

          {/* Subheadline */}
          <motion.p
            initial={{ opacity: 0, y: 26 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="text-[15px] sm:text-lg text-background/70 mb-5 sm:mb-6 max-w-2xl leading-relaxed mx-auto"
          >
            Square Trigger helps businesses build high-performing websites and
            implement strategic SEO that drives measurable growth across
            Vancouver and throughout Canada.
          </motion.p>

          {/* Highlights */}
          <motion.div
            initial={{ opacity: 0, y: 26 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="mb-6 sm:mb-8"
          >
            <div className="mx-auto max-w-md">
              <ul className="space-y-2.5 inline-block text-left">
                {highlights.map((highlight, index) => (
                  <li
                    key={index}
                    className="flex items-start gap-3 text-background/80"
                  >
                    <CheckCircle className="h-5 w-5 text-secondary shrink-0 mt-[2px]" />
                    <span className="text-sm leading-relaxed">{highlight}</span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>

          {/* CTAs */}
          <motion.div
            initial={{ opacity: 0, y: 26 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="flex flex-col sm:flex-row gap-3 sm:gap-4 items-center justify-center"
          >
            <Button
              variant="gradient"
              size="lg"
              asChild
              className="group w-full sm:w-auto justify-center"
            >
              <Link
                to="/get-started"
                className="flex items-center justify-center gap-2"
              >
                Get Started Today
                <ArrowRight className="h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Link>
            </Button>

            <Button
              variant="glass"
              size="lg"
              asChild
              className="w-full sm:w-auto justify-center text-white bg-white/10 border border-white/20 hover:shadow-[0_8px_30px_rgba(255,255,255,0.12)] hover:-translate-y-[1px] transition-all duration-300"
            >
              <Link
                to="/portfolio/case-studies"
                className="flex items-center justify-center gap-2"
              >
                <Play className="h-5 w-5" />
                View Our Work
              </Link>
            </Button>
          </motion.div>
        </div>
      </div>

      {/* Scroll Indicator
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
        className="hidden lg:block absolute bottom-4 left-1/2 -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
          className="flex flex-col items-center gap-1.5 text-background/50"
        >
          <span className="text-xs">Scroll to explore</span>
          <div className="w-5 h-9 border-2 border-background/30 rounded-full flex justify-center pt-2">
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-1.5 h-1.5 bg-background/50 rounded-full"
            />
          </div>
        </motion.div>
      </motion.div> */}
    </section>
  );
};